/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

/**
 * DeleteContentService - Handles removing uploaded content
 * API: POST /api/v2.0/upload/content/:fileId
 */
export default class DeleteContentService extends BaseService {
  constructor() {
    super(constants.DELETE_CONTENT, 'deleteContentStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { fileId } = payload;

    // Store fileId on the instance so handleResponse can use it as a fallback
    // if the API response does not echo the fileId back (same pattern as UploadContentService)
    this.fileId = fileId;

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackbv2').post(`/upload/content/${fileId}`, {}, { headers: enhancedHeaders });
    }
    return this.getClient('aiadminv2').post(`/upload/content/${fileId}`, {}, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      const fileId = data.data?.fileId || this.fileId;
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'success',
        langMessage: 'contentUpload.DELETE_SUCCESS'
      });
      Framework.getStoreRegistry().getStore('contentUploadModel').setDeleteSuccess(fileId);
      Framework.getEventManager().publish(constants.DELETE_CONTENT_SUCCESS, { data: data.data });
    } else {
      const serverMessage = data?.error || data?.message;
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          ...(serverMessage ? { message: serverMessage } : { langMessage: 'contentUpload.DELETE_FAILED' })
        });
      Framework.getStoreRegistry()
        .getStore('contentUploadModel')
        .setDeleteError(serverMessage || 'contentUpload.DELETE_FAILED');
      Framework.getEventManager().publish(constants.DELETE_CONTENT_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
        Framework.getEventManager().publish(constants.DELETE_CONTENT_ERROR);
        return;
      }
      const serverMessage = data?.error || data?.message;
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          ...(serverMessage ? { message: serverMessage } : { langMessage: 'contentUpload.DELETE_REQUEST_FAILED' })
        });
      Framework.getStoreRegistry()
        .getStore('contentUploadModel')
        .setDeleteError(serverMessage || 'contentUpload.DELETE_REQUEST_FAILED');
      Framework.getEventManager().publish(constants.DELETE_CONTENT_ERROR);
      return;
    }

    const langMessage =
      error.message === `timeout of ${constants.BERLIN_API_TIMEOUT}ms exceeded`
        ? 'contentUpload.REQUEST_TIMEOUT'
        : 'contentUpload.DELETE_REQUEST_FAILED';

    Framework.getStoreRegistry().getStore('notificationState').set({
      open: true,
      type: 'error',
      langMessage
    });
    Framework.getStoreRegistry().getStore('contentUploadModel').setDeleteError(langMessage);
    Framework.getEventManager().publish(constants.DELETE_CONTENT_ERROR);
  }
}
